/**
 * TicketButton - "Submit a ticket" at the bottom of the sidebar (Sharif,
 * 10/6/26). Anyone signed in can report a bug or send an idea, with files:
 * drag and drop, pick, paste an image (Win+Shift+S then Ctrl+V), or
 * "Screenshot this page". POST /api/feedback saves it (the Tickets page)
 * and emails it to Sharif; the answer names the ticket number. "My tickets"
 * lists the person's own tickets and how each is going (GET /api/tickets/mine).
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { acceptDrag, filesOf } from '../../utils/droppedFiles';
import { json } from '../../utils/api';
import { STATUS_LABELS } from '../../views/tickets/ticketsRules';
import { ticketsChanged } from '../../views/tickets/useOpenTicketCount';

const MAX_FILES = 8;
const MAX_TOTAL_MB = 18; // matches server/routes/feedback.js

export function TicketButton({ collapsed = false, page = '' }) {
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [kind, setKind] = useState('bug');
  const [subject, setSubject] = useState('');
  const [details, setDetails] = useState('');
  const [files, setFiles] = useState([]); // [{ file, url }]
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { tone, text }
  const [mine, setMine] = useState(null); // null = closed, else { rows } or { error }
  const pick = useRef(null);

  const reset = () => {
    files.forEach((f) => f.url && URL.revokeObjectURL(f.url));
    setKind('bug');
    setSubject('');
    setDetails('');
    setFiles([]);
    setMsg(null);
  };

  const add = useCallback((list) => {
    const incoming = Array.from(list || []).filter(Boolean);
    if (!incoming.length) return;
    setMsg(null);
    setFiles((cur) => {
      const next = [
        ...cur,
        ...incoming.map((file) => ({
          file,
          url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        })),
      ];
      if (next.length > MAX_FILES) setMsg({ tone: 'err', text: `At most ${MAX_FILES} files - extra ones were left off.` });
      return next.slice(0, MAX_FILES);
    });
  }, []);

  // Pasting an image anywhere while the form is open attaches it.
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      const imgs = filesOf(e.clipboardData, { renameGeneric: true });
      if (imgs.length) {
        e.preventDefault();
        add(imgs);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, add]);

  // Capture what is on screen, leaving this form (the top-most dialog) out
  // of the picture - any other window open underneath stays in it.
  const screenshot = async () => {
    setCapturing(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const mine = [...document.querySelectorAll('[role="dialog"]')].pop();
      const canvas = await html2canvas(document.body, {
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        scale: 1,
        logging: false,
        ignoreElements: (el) => el === mine,
      });
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
      if (blob)
        add([new File([blob], `screenshot-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`, { type: 'image/png' })]);
    } catch {
      setMsg({ tone: 'err', text: 'Could not take the screenshot - use Win+Shift+S and paste it here instead.' });
    } finally {
      setCapturing(false);
    }
  };

  const totalMb = files.reduce((n, f) => n + f.file.size, 0) / 1024 / 1024;
  const canSend = !busy && (subject.trim() || details.trim()) && totalMb <= MAX_TOTAL_MB;

  const toggleMine = async () => {
    if (mine) return setMine(null);
    const r = await json('/api/tickets/mine');
    setMine(r.ok && r.body ? { rows: r.body.rows || [] } : { error: 'Could not load your tickets.' });
    return undefined;
  };

  const send = async () => {
    setBusy(true);
    setMsg(null);
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('subject', subject);
    fd.append('details', details);
    fd.append('page', `${page ? `${page} - ` : ''}${window.location.href}`);
    files.forEach((f) => fd.append('files', f.file));
    try {
      const r = await fetch('/api/feedback', { method: 'POST', body: fd });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Could not send the ticket.');
      reset();
      ticketsChanged();
      // saved either way; the server says whether it was also emailed
      setMsg({ tone: 'ok', text: body.message || `Saved as ${body.number}.` });
      setTimeout(() => {
        setOpen(false);
        setMsg(null);
      }, 4000);
    } catch (e) {
      setMsg({ tone: 'err', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Submit a ticket - report a bug or send an idea"
        className={clsx(
          'w-full flex items-center rounded text-[12px] font-semibold text-white/60 hover:bg-white/5 hover:text-white transition-colors mb-2',
          collapsed ? 'justify-center py-2' : 'gap-2.5 px-3 py-2'
        )}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <line x1="12" y1="7" x2="12" y2="11" />
          <line x1="12" y1="14" x2="12.01" y2="14" />
        </svg>
        {!collapsed && <span>Submit a ticket</span>}
      </button>

      <Modal
        open={open}
        onClose={() => !busy && setOpen(false)}
        title="Submit a ticket"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={send} disabled={!canSend}>
              {busy ? 'Sending...' : 'Send'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-[12px]">
          <div className="flex gap-1.5" role="radiogroup" aria-label="Ticket type">
            {[
              ['bug', 'Report a bug'],
              ['idea', 'Idea / improvement'],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={kind === k}
                onClick={() => setKind(k)}
                className={clsx(
                  'px-3 py-1.5 rounded border font-semibold',
                  kind === k
                    ? 'bg-gat-green text-white border-gat-green'
                    : 'bg-surface border-border-strong text-text-body hover:bg-surface-2'
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="block">
            <span className="font-semibold text-text-dark">Subject</span>
            <input
              className="mt-1 w-full border border-border-strong rounded px-2.5 py-1.5"
              value={subject}
              maxLength={150}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={kind === 'bug' ? 'What went wrong?' : 'What would make it better?'}
            />
          </label>
          <label className="block">
            <span className="font-semibold text-text-dark">Details</span>
            <textarea
              className="mt-1 w-full border border-border-strong rounded px-2.5 py-1.5 min-h-[100px]"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={kind === 'bug' ? 'What did you do, what did you expect, and what happened instead?' : 'Describe the idea.'}
            />
          </label>

          <div
            onDragOver={(e) => {
              acceptDrag(e);
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              add(filesOf(e.dataTransfer));
            }}
            className={clsx(
              'border-2 border-dashed rounded p-3 text-center text-text-body',
              drag ? 'border-gat-green bg-gat-green/5' : 'border-border-strong'
            )}
          >
            <div>Drag files here, paste an image (Ctrl+V), or</div>
            <div className="mt-2 flex justify-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => pick.current?.click()}>
                Choose files
              </Button>
              <Button size="sm" variant="secondary" onClick={screenshot} disabled={capturing}>
                {capturing ? 'Capturing...' : 'Screenshot this page'}
              </Button>
            </div>
            <input
              ref={pick}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                add(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          {files.length > 0 && (
            <ul className="space-y-1.5">
              {files.map((f, i) => (
                <li key={`${f.file.name}-${i}`} className="flex items-center gap-2">
                  {f.url ? (
                    <img src={f.url} alt="" className="w-12 h-9 object-cover rounded border border-border-strong" />
                  ) : (
                    <span className="w-12 h-9 rounded border border-border-strong flex items-center justify-center text-[10px]">file</span>
                  )}
                  <span className="flex-1 truncate">{f.file.name}</span>
                  <span className="text-text-muted">{(f.file.size / 1024 / 1024).toFixed(1)} MB</span>
                  <button
                    type="button"
                    aria-label={`Remove ${f.file.name}`}
                    className="px-1.5 text-text-muted hover:text-status-lost"
                    onClick={() =>
                      setFiles((cur) => {
                        if (cur[i].url) URL.revokeObjectURL(cur[i].url);
                        return cur.filter((_, j) => j !== i);
                      })
                    }
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          {totalMb > MAX_TOTAL_MB && (
            <div className="text-status-lost">
              Files add up to {totalMb.toFixed(1)} MB - the limit is {MAX_TOTAL_MB} MB.
            </div>
          )}
          <div className="border-t border-border pt-2">
            <button type="button" className="text-gat-green font-semibold hover:underline" onClick={toggleMine}>
              {mine ? 'Hide my tickets' : 'My tickets'}
            </button>
            {mine && mine.error && <div className="text-status-lost mt-1">{mine.error}</div>}
            {mine && mine.rows && (
              <ul className="mt-1.5 space-y-1 max-h-40 overflow-y-auto" aria-label="My tickets">
                {!mine.rows.length && <li className="text-text-muted">You have not sent any tickets yet.</li>}
                {mine.rows.map((t) => (
                  <li key={t.id} className="flex gap-2">
                    <span className="font-semibold whitespace-nowrap">{t.number}</span>
                    <span className="flex-1 truncate" title={t.resolution_note || ''}>
                      {t.subject || '(no subject)'}
                    </span>
                    <span className="text-text-muted whitespace-nowrap">{STATUS_LABELS[t.status] || t.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {msg && (
            <div role="status" className={msg.tone === 'ok' ? 'text-gat-green font-semibold' : 'text-status-lost'}>
              {msg.text}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
